import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ChevronLeft, Camera, Plus, Info, Sparkles, Check, X, Loader2, CheckCircle2, XCircle, Clock } from "lucide-react";
import { api } from "../lib/api";
import { useToast } from "../components/Toast";
import { useAuth } from "../lib/auth";
import { pickDemoOutcome, setPendingDemoBonus } from "../components/DemoFlow";
import { playWin } from "../lib/sounds";

// Decorative tilted "RECEIPT" sketch — sits behind the upload card.
function ReceiptSketch({ flip = false, label = "RECEIPT", price = "£13.00" }) {
  return (
    <div
      aria-hidden="true"
      style={{
        width: 130, height: 175,
        background: "var(--surface)",
        borderRadius: 8,
        border: "1px solid var(--primary-soft)",
        position: "relative",
        opacity: 0.55,
        transform: flip ? "rotate(14deg)" : "rotate(-12deg)",
        boxShadow: "0 14px 30px -22px rgba(247,122,42,0.45)",
        overflow: "hidden",
      }}
    >
      <div style={{
        position: "absolute", top: 16, left: 14,
        fontWeight: 900, fontSize: 12, letterSpacing: 1.2,
        color: "var(--primary-soft)",
      }}>{label}</div>
      <div style={{ position: "absolute", top: 44, left: 14, right: 14 }}>
        {[1,2,3].map((i) => (
          <div key={i} style={{ height: 6, background: "var(--primary-soft)", borderRadius: 3, opacity: 0.55, marginBottom: 7, width: i===2?"70%":"100%" }} />
        ))}
      </div>
      <div style={{
        position: "absolute", bottom: 30, left: 14,
        fontSize: 11, color: "var(--primary-soft)", fontWeight: 800, letterSpacing: 0.8,
      }}>TOTAL</div>
      <div style={{
        position: "absolute", bottom: 12, left: 14,
        fontSize: 16, color: "var(--primary-soft)", fontWeight: 900,
      }}>{price}</div>
      {/* zigzag bottom */}
      <svg viewBox="0 0 130 8" style={{ position: "absolute", bottom: -1, left: 0, width: "100%" }}>
        <path d="M0,0 L8,8 L16,0 L24,8 L32,0 L40,8 L48,0 L56,8 L64,0 L72,8 L80,0 L88,8 L96,0 L104,8 L112,0 L120,8 L128,0 L130,4 L130,0 Z" fill="var(--bg)" />
      </svg>
    </div>
  );
}

export default function Upload() {
  const navigate = useNavigate();
  const toast = useToast();
  const { user, refresh } = useAuth();
  const [searchParams] = useSearchParams();
  const isDemo = searchParams.get("demo") === "1" && !user;
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [previewDataUri, setPreviewDataUri] = useState(null);
  const [showEligibility, setShowEligibility] = useState(false);
  // result === null  -> no result yet
  // result.status: 'approved' | 'rejected' | 'pending'
  const [result, setResult] = useState(null);

  // In demo mode, auto-pre-load the sample receipt so the user can just hit Confirm.
  useEffect(() => {
    if (isDemo && !previewDataUri) {
      setPreviewDataUri("/demo/sample-receipt.svg");
    }
  }, [isDemo, previewDataUri]);

  const compressImage = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onerror = () => reject(reader.error);
      reader.onload = () => {
        const img = new Image();
        img.onload = () => {
          try {
            const MAX = 1280;
            let { width, height } = img;
            if (width > height && width > MAX) { height = Math.round(height * (MAX / width)); width = MAX; }
            else if (height > width && height > MAX) { width = Math.round(width * (MAX / height)); height = MAX; }
            else if (width === height && width > MAX) { width = height = MAX; }
            const canvas = document.createElement("canvas");
            canvas.width = width; canvas.height = height;
            const ctx = canvas.getContext("2d");
            ctx.imageSmoothingQuality = "high";
            ctx.drawImage(img, 0, 0, width, height);
            resolve(canvas.toDataURL("image/jpeg", 0.78));
          } catch (err) { reject(err); }
        };
        img.onerror = () => reject(new Error("Could not read that image."));
        img.src = reader.result;
      };
      reader.readAsDataURL(file);
    });

  // User picked a file — show preview, don't upload yet.
  const handleFile = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Please select an image file."); return; }
    try {
      const dataUri = await compressImage(file);
      setPreviewDataUri(dataUri);
    } catch (e) {
      toast.error(e?.message || "Could not read that image.");
    } finally {
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  // User confirmed — actually send it.
  const submitPreview = async () => {
    if (!previewDataUri) return;
    if (isDemo) {
      // Demo mode: fake the scan & approval — pick a random £1-£2 amount
      // and stash it locally so we can credit it on sign-up.
      setUploading(true);
      await new Promise((r) => setTimeout(r, 2200));
      setUploading(false);
      const demoAmount = pickDemoOutcome();
      setPendingDemoBonus(demoAmount);
      setResult({
        status: "approved",
        awarded_cash: demoAmount,
        reason: null,
        ai: { brand: "mcdonalds", total_amount: 13.19 },
      });
      playWin();
      return;
    }
    try {
      setUploading(true);
      const res = await api.uploadReceipt(previewDataUri);
      await refresh();
      setResult(res);
      if (res?.status === "approved" && (res.awarded_cash || 0) > 0) {
        playWin();
      }
    } catch (e) {
      toast.error(e?.message || "Upload failed.");
    } finally {
      setUploading(false);
    }
  };

  const resetForAnotherUpload = () => {
    setResult(null);
    setPreviewDataUri(null);
    setTimeout(() => fileRef.current?.click(), 50);
  };

  const retake = () => {
    setPreviewDataUri(null);
    // Re-open the picker
    setTimeout(() => fileRef.current?.click(), 50);
  };

  return (
    <div className="fade-in" style={{ background: "var(--bg)", minHeight: "100vh", paddingBottom: 120 }}>
      <div style={{ maxWidth: 560, margin: "0 auto", padding: "max(18px, env(safe-area-inset-top)) 16px 0" }}>
        <button
          onClick={() => navigate(-1)}
          data-testid="upload-back-button"
          style={{
            width: 44, height: 44, borderRadius: 22,
            background: "var(--surface)", border: "1px solid var(--border)",
            display: "flex", alignItems: "center", justifyContent: "center",
            boxShadow: "0 8px 24px -16px rgba(11,36,71,0.2)",
          }}
        >
          <ChevronLeft size={22} color="var(--text-primary)" />
        </button>

        <h1 style={{
          textAlign: "center",
          fontSize: 22, fontWeight: 800, color: "var(--navy)",
          letterSpacing: -0.4, marginTop: 18, marginBottom: 4,
        }}>
          Upload receipt to earn cash
        </h1>
        <p style={{
          textAlign: "center", color: "var(--text-secondary)",
          fontSize: 13, lineHeight: 1.45, marginTop: 6, marginBottom: 10,
          padding: "0 12px",
        }}>
          Snap a quick photo of your receipt — we'll drop cash straight into your wallet.
        </p>

        {isDemo && (
          <div
            data-testid="upload-demo-banner"
            style={{
              display: "flex", alignItems: "center", gap: 10,
              background: "var(--primary)", color: "#FFFFFF",
              borderRadius: 14, padding: "10px 14px",
              margin: "10px auto 14px", maxWidth: 480,
              boxShadow: "0 14px 28px -18px rgba(247,122,42,0.55)",
            }}
          >
            <span style={{
              width: 26, height: 26, borderRadius: 13, flexShrink: 0,
              background: "rgba(255,255,255,0.22)",
              border: "1px solid rgba(255,255,255,0.35)",
              display: "flex", alignItems: "center", justifyContent: "center",
              fontSize: 11, fontWeight: 900,
            }}>1</span>
            <div style={{ fontSize: 12.5, lineHeight: 1.4, fontWeight: 700 }}>
              We've loaded a sample receipt for you.<br />
              Tap <b>Send Receipt</b> below to see how it works.
            </div>
          </div>
        )}

        <div style={{ position: "relative" }}>
          {/* Decorative receipts */}
          <div style={{ position: "absolute", left: -50, top: 18, pointerEvents: "none" }}>
            <ReceiptSketch />
          </div>
          <div style={{ position: "absolute", right: -50, top: 60, pointerEvents: "none" }}>
            <ReceiptSketch flip price="£11.50" />
          </div>

          {/* Upload card — compact (hidden when reviewing a preview) */}
          {!previewDataUri && (
          <div
            data-testid="upload-card"
            style={{
              position: "relative", zIndex: 1,
              background: "var(--surface)",
              border: "1px solid var(--border)",
              borderRadius: 24,
              padding: "18px 16px",
              boxShadow: "0 22px 50px -28px rgba(11,36,71,0.25)",
              textAlign: "center",
            }}
          >
            {/* Reward badge — solid green pill for max clarity */}
            <div style={{
              display: "inline-flex", alignItems: "center", gap: 6,
              background: "#1FAE5E",
              border: "1px solid #1FAE5E",
              borderRadius: 999, padding: "8px 16px",
              boxShadow: "0 10px 22px -14px rgba(31, 174, 94, 0.55)",
            }}>
              <span style={{ color: "#FFFFFF", fontSize: 12.5, fontWeight: 900, letterSpacing: 0.4 }}>
                Earn up to £5 per receipt
              </span>
            </div>

            {/* Colour-coded info tiles */}
            <div style={{
              display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10,
              marginTop: 14, textAlign: "left",
            }}>
              <div style={{
                background: "rgba(247, 122, 42, 0.10)",
                border: "1px solid rgba(247, 122, 42, 0.35)",
                borderRadius: 14, padding: "10px 12px",
              }}>
                <div style={{
                  color: "var(--primary)", fontSize: 10, fontWeight: 800,
                  letterSpacing: 0.8, textTransform: "uppercase",
                }}>
                  Accepted brands
                </div>
                <div style={{
                  color: "var(--navy)", fontSize: 13, fontWeight: 800,
                  marginTop: 4, lineHeight: 1.25,
                }}>
                  McDonald's, KFC, Popeyes
                </div>
              </div>

              <div style={{
                background: "rgba(230, 59, 90, 0.10)",
                border: "1px solid rgba(230, 59, 90, 0.35)",
                borderRadius: 14, padding: "10px 12px",
              }}>
                <div style={{
                  color: "var(--accent-pink)", fontSize: 10, fontWeight: 800,
                  letterSpacing: 0.8, textTransform: "uppercase",
                }}>
                  Min value &amp; receipt age
                </div>
                <div style={{
                  color: "var(--navy)", fontSize: 13, fontWeight: 800,
                  marginTop: 4, lineHeight: 1.25,
                }}>
                  £9+ &amp; within last 3 days
                </div>
              </div>
            </div>

            {/* Camera icon — solid brand-orange filled circle for clarity */}
            <div style={{
              position: "relative",
              width: 96, height: 96, margin: "18px auto 14px",
              display: "flex", alignItems: "center", justifyContent: "center",
            }}>
              <div style={{
                position: "absolute", inset: 0, borderRadius: 48,
                background: "var(--primary)",
                boxShadow: "0 18px 32px -14px rgba(247, 122, 42, 0.55)",
              }} />
              <Camera size={36} color="#FFFFFF" style={{ position: "relative", zIndex: 2 }} />
              <span style={{
                position: "absolute", top: 4, right: 4,
                width: 24, height: 24, borderRadius: 12,
                background: "#FFC72C",
                color: "var(--navy)", border: "2px solid var(--surface)",
                display: "flex", alignItems: "center", justifyContent: "center",
                zIndex: 3, boxShadow: "0 6px 14px -6px rgba(255,199,44,0.7)",
              }}>
                <Plus size={13} />
              </span>
            </div>

            <input
              ref={fileRef}
              type="file"
              accept="image/*"
              style={{ display: "none" }}
              onChange={(e) => handleFile(e.target.files?.[0])}
              data-testid="upload-file-input"
            />
            <button
              data-testid="upload-take-photo-button"
              className="btn-primary"
              onClick={() => fileRef.current?.click()}
              disabled={uploading}
              style={{
                width: "100%",
                display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 10,
                fontSize: 13, fontWeight: 900, letterSpacing: 1.2, textTransform: "uppercase",
                padding: "14px 18px",
              }}
            >
              <Camera size={16} />
              {uploading ? "Uploading…" : "Upload / Take Photo"}
            </button>
          </div>
          )}

          {/* Preview / review step — shown after user picks a file, before send */}
          {previewDataUri && (
            <div
              data-testid="upload-preview-card"
              style={{
                position: "relative", zIndex: 1,
                background: "var(--surface)",
                border: "1px solid var(--border)",
                borderRadius: 24,
                padding: "18px 16px",
                boxShadow: "0 22px 50px -28px rgba(11,36,71,0.25)",
                textAlign: "center",
              }}
            >
              <div style={{
                color: "var(--navy)", fontWeight: 800, fontSize: 15, marginBottom: 12,
              }}>
                Review your receipt before sending
              </div>

              <div style={{
                width: "100%",
                background: "var(--surface-elev)",
                border: "1px solid var(--border)",
                borderRadius: 16,
                overflow: "hidden",
                display: "flex", alignItems: "center", justifyContent: "center",
                maxHeight: 360,
              }}>
                <img
                  data-testid="upload-preview-image"
                  src={previewDataUri}
                  alt="Receipt preview"
                  style={{ width: "100%", maxHeight: 360, objectFit: "contain", display: "block" }}
                />
              </div>

              <div style={{ display: "flex", gap: 10, marginTop: 14 }}>
                <button
                  data-testid="upload-preview-retake"
                  onClick={retake}
                  disabled={uploading}
                  style={{
                    flex: 1,
                    display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 8,
                    background: "var(--surface)", color: "var(--text-primary)",
                    border: "1px solid var(--border-strong)",
                    borderRadius: 999, padding: "14px 16px",
                    fontWeight: 800, fontSize: 13, letterSpacing: 0.4,
                    opacity: uploading ? 0.6 : 1,
                  }}
                >
                  <X size={16} />
                  Retake
                </button>
                <button
                  data-testid="upload-preview-confirm"
                  onClick={submitPreview}
                  disabled={uploading}
                  className="btn-primary"
                  style={{
                    flex: 1,
                    display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 8,
                    padding: "14px 16px",
                    fontSize: 13, fontWeight: 900, letterSpacing: 0.8, textTransform: "uppercase",
                  }}
                >
                  <Check size={16} />
                  {uploading ? "Sending…" : "Send Receipt"}
                </button>
              </div>
            </div>
          )}

          {/* Accepted places banner removed — brand list is now inside the upload card info tiles above. */}

          <button
            data-testid="upload-eligibility-button"
            onClick={() => setShowEligibility(true)}
            style={{
              display: "inline-flex", alignItems: "center", gap: 6,
              margin: "16px auto 0", padding: "10px 16px",
              background: "var(--surface)",
              border: "1px solid var(--border-strong)",
              borderRadius: 999,
              color: "var(--text-primary)",
              fontSize: 13, fontWeight: 800, letterSpacing: 0.2,
              cursor: "pointer",
              boxShadow: "0 10px 22px -16px rgba(11,36,71,0.2)",
            }}
          >
            <Info size={15} color="var(--primary)" />
            What counts as a valid receipt?
          </button>
        </div>
      </div>

      {showEligibility && <EligibilityModal onClose={() => setShowEligibility(false)} />}

      {uploading && <ScanningOverlay imageSrc={previewDataUri} />}
      {result && (
        <ResultOverlay
          result={result}
          onTryAgain={resetForAnotherUpload}
          onDone={() => navigate(isDemo ? "/register" : "/", { replace: true })}
          isDemo={isDemo}
        />
      )}
    </div>
  );
}

/* ---------- AI Scanning loading overlay ---------- */
function ScanningOverlay({ imageSrc }) {
  return (
    <div
      data-testid="upload-scanning-overlay"
      style={{
        position: "fixed", inset: 0, zIndex: 200,
        background: "rgba(14, 34, 64, 0.78)",
        display: "flex", alignItems: "center", justifyContent: "center",
        padding: 24, backdropFilter: "blur(6px)", WebkitBackdropFilter: "blur(6px)",
      }}
    >
      <style>{`
        @keyframes cb-scan-line {
          0%   { transform: translateY(-100%); opacity: 0.0; }
          15%  { opacity: 1; }
          85%  { opacity: 1; }
          100% { transform: translateY(220%); opacity: 0.0; }
        }
        @keyframes cb-scan-pulse {
          0%, 100% { transform: scale(1); opacity: 0.55; }
          50%      { transform: scale(1.04); opacity: 0.9; }
        }
      `}</style>
      <div style={{
        width: "100%", maxWidth: 360,
        background: "var(--surface)", borderRadius: 24,
        padding: 22, textAlign: "center",
        boxShadow: "0 30px 60px -24px rgba(0,0,0,0.4)",
      }}>
        <div style={{
          position: "relative", width: 200, height: 240,
          margin: "0 auto 18px", borderRadius: 16,
          overflow: "hidden", background: "var(--surface-elev)",
          border: "2px solid var(--primary)",
          boxShadow: "0 0 0 6px rgba(247,122,42,0.18)",
          animation: "cb-scan-pulse 1.8s ease-in-out infinite",
        }}>
          {imageSrc && (
            <img
              src={imageSrc}
              alt="Scanning"
              style={{
                width: "100%", height: "100%", objectFit: "cover",
                filter: "saturate(0.9)",
              }}
            />
          )}
          {/* Scan line */}
          <div style={{
            position: "absolute", top: 0, left: 0, right: 0, height: 4,
            background: "linear-gradient(90deg, transparent, #F77A2A 30%, #FFC72C 50%, #F77A2A 70%, transparent)",
            boxShadow: "0 0 20px #F77A2A, 0 0 40px rgba(247,122,42,0.55)",
            animation: "cb-scan-line 2s linear infinite",
          }} />
          {/* Corner brackets */}
          {[
            { top: 8, left: 8, borderTop: "3px solid #FFC72C", borderLeft: "3px solid #FFC72C" },
            { top: 8, right: 8, borderTop: "3px solid #FFC72C", borderRight: "3px solid #FFC72C" },
            { bottom: 8, left: 8, borderBottom: "3px solid #FFC72C", borderLeft: "3px solid #FFC72C" },
            { bottom: 8, right: 8, borderBottom: "3px solid #FFC72C", borderRight: "3px solid #FFC72C" },
          ].map((s, i) => (
            <div key={i} style={{ position: "absolute", width: 18, height: 18, ...s }} />
          ))}
        </div>
        <div style={{
          display: "inline-flex", alignItems: "center", gap: 8,
          marginBottom: 6, color: "var(--primary)",
        }}>
          <Loader2 size={16} className="spin" style={{ animation: "cb-spin 1s linear infinite" }} />
          <style>{`@keyframes cb-spin { from { transform: rotate(0); } to { transform: rotate(360deg); } }`}</style>
          <span style={{ fontSize: 12, fontWeight: 800, letterSpacing: 0.6, textTransform: "uppercase" }}>
            Scanning receipt
          </span>
        </div>
        <div style={{ color: "var(--navy)", fontSize: 16, fontWeight: 800, lineHeight: 1.3 }}>
          Reading total, date and brand…
        </div>
        <div style={{ color: "var(--text-secondary)", fontSize: 12, marginTop: 6, lineHeight: 1.45 }}>
          This usually takes 3–5 seconds.
        </div>
      </div>
    </div>
  );
}

/* ---------- Result overlay (Approved / Rejected / In review) ---------- */
function ResultOverlay({ result, onTryAgain, onDone, isDemo }) {
  const { status, reason, awarded_cash, ai } = result;
  const cash = Number(awarded_cash || 0);

  let icon, iconBg, iconColor, title, subtitle, primaryLabel, primaryAction, showSecondary, showConfetti = false;
  if (status === "approved") {
    icon = <CheckCircle2 size={56} color="#FFFFFF" strokeWidth={2.4} />;
    iconBg = "#1FAE5E";
    iconColor = "#1FAE5E";
    title = "Receipt approved!";
    if (isDemo) {
      subtitle = "Sign up to claim your cashback into your real wallet.";
      primaryLabel = "Sign up & claim";
      primaryAction = onDone; // Upload.js re-routes demo Done to /register
    } else {
      subtitle = "Upload another receipt to earn more.";
      primaryLabel = "Upload another";
      primaryAction = onTryAgain;
    }
    showSecondary = true;
    showConfetti = true;
  } else if (status === "pending") {
    icon = <Clock size={56} color="#FFFFFF" strokeWidth={2.2} />;
    iconBg = "var(--primary)";
    iconColor = "var(--primary)";
    title = "Receipt in review";
    subtitle = reason || "Our team will take a closer look and you'll get a notification shortly.";
    primaryLabel = "Got it";
    primaryAction = onDone;
    showSecondary = false;
  } else {
    // rejected
    icon = <XCircle size={56} color="#FFFFFF" strokeWidth={2.4} />;
    iconBg = "#E63B5A";
    iconColor = "#E63B5A";
    title = "Receipt rejected";
    subtitle = reason || "Sorry, your receipt couldn't be accepted.";
    primaryLabel = "Try again";
    primaryAction = onTryAgain;
    showSecondary = true;
  }

  return (
    <div
      data-testid={`upload-result-${status}`}
      style={{
        position: "fixed", inset: 0, zIndex: 210,
        background: "rgba(14, 34, 64, 0.78)",
        display: "flex", alignItems: "center", justifyContent: "center",
        padding: 24, backdropFilter: "blur(6px)", WebkitBackdropFilter: "blur(6px)",
        overflow: "hidden",
      }}
    >
      {showConfetti && <Confetti />}
      <div style={{
        width: "100%", maxWidth: 380,
        background: "var(--surface)", borderRadius: 28,
        padding: "26px 22px 22px", textAlign: "center",
        boxShadow: "0 30px 60px -24px rgba(0,0,0,0.4)",
        position: "relative", zIndex: 1,
        animation: status === "approved" ? "reward-pop 380ms cubic-bezier(0.16,1,0.3,1)" : undefined,
      }}>
        <div style={{
          width: 92, height: 92, borderRadius: 46,
          background: iconBg,
          display: "inline-flex", alignItems: "center", justifyContent: "center",
          marginBottom: 14,
          boxShadow: `0 18px 32px -16px ${iconBg}`,
          animation: status === "approved" ? "reward-bounce 700ms ease-out" : undefined,
        }}>
          {icon}
        </div>
        <h2 style={{
          color: "var(--navy)", fontSize: 22, fontWeight: 800,
          letterSpacing: -0.4, margin: "0 0 8px",
        }}>
          {title}
        </h2>

        {status === "approved" && cash > 0 && (
          <div
            data-testid="upload-result-cash-amount"
            style={{
              margin: "6px 0 10px",
              display: "flex", flexDirection: "column", alignItems: "center",
            }}
          >
            <div style={{
              fontSize: 12, fontWeight: 800, letterSpacing: 1.2, textTransform: "uppercase",
              color: "#1FAE5E",
            }}>
              Added to your wallet
            </div>
            <div style={{
              fontSize: 44, fontWeight: 900, letterSpacing: -2, color: "#1FAE5E",
              lineHeight: 1.05, marginTop: 2,
              animation: "reward-count 500ms ease-out",
            }}>
              +£{cash.toFixed(2)}
            </div>
          </div>
        )}

        <p style={{
          color: "var(--text-secondary)", fontSize: 14, lineHeight: 1.5,
          margin: "0 0 6px", padding: "0 6px",
        }}>
          {subtitle}
        </p>

        {status === "approved" && ai?.brand && ai?.total_amount && (
          <div style={{
            background: "var(--peach-bg-soft)", borderRadius: 14,
            border: "1px solid var(--peach-border)",
            padding: "10px 14px", margin: "14px 0 6px",
            display: "flex", justifyContent: "space-between",
            fontSize: 13, fontWeight: 700, color: "var(--text-primary)",
          }}>
            <span style={{ textTransform: "capitalize" }}>{ai.brand.replace("mcdonalds", "McDonald's")}</span>
            <span>£{Number(ai.total_amount).toFixed(2)}</span>
          </div>
        )}

        <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 18 }}>
          <button
            data-testid="upload-result-primary"
            onClick={primaryAction}
            className="btn-primary"
            style={{
              width: "100%",
              padding: "14px 16px",
              fontSize: 13, fontWeight: 900, letterSpacing: 0.8, textTransform: "uppercase",
              background: iconColor, borderColor: iconColor,
            }}
          >
            {primaryLabel}
          </button>
          {showSecondary && (
            <button
              data-testid="upload-result-secondary"
              onClick={onDone}
              style={{
                background: "transparent", color: "var(--text-secondary)",
                border: "none", padding: "8px 16px",
                fontSize: 13, fontWeight: 700,
              }}
            >
              Close
            </button>
          )}
        </div>
      </div>
      <style>{`
        @keyframes reward-pop {
          0% { transform: scale(0.85); opacity: 0; }
          100% { transform: scale(1); opacity: 1; }
        }
        @keyframes reward-bounce {
          0%   { transform: scale(0.4);  }
          55%  { transform: scale(1.15); }
          75%  { transform: scale(0.95); }
          100% { transform: scale(1);    }
        }
        @keyframes reward-count {
          0%   { transform: scale(0.6); opacity: 0; }
          60%  { transform: scale(1.12); opacity: 1; }
          100% { transform: scale(1); opacity: 1; }
        }
      `}</style>
    </div>
  );
}

/* Confetti burst — CSS-only. 40 tiny coloured squares fall from the top. */
function Confetti() {
  const pieces = 40;
  const colors = ["#F77A2A", "#E63B5A", "#2EBFA6", "#FFC72C", "#4F46E5", "#FFA351"];
  return (
    <div aria-hidden style={{
      position: "absolute", inset: 0, pointerEvents: "none", overflow: "hidden",
    }}>
      {Array.from({ length: pieces }).map((_, i) => {
        const left = Math.random() * 100;
        const delay = Math.random() * 0.5;
        const duration = 1.6 + Math.random() * 1.4;
        const rot = Math.random() * 360;
        const size = 6 + Math.floor(Math.random() * 6);
        const color = colors[i % colors.length];
        return (
          <span
            key={i}
            style={{
              position: "absolute", top: -14, left: `${left}%`,
              width: size, height: size * 0.4,
              background: color, borderRadius: 1,
              transform: `rotate(${rot}deg)`,
              animation: `confetti-fall ${duration}s ${delay}s linear forwards`,
              opacity: 0.9,
            }}
          />
        );
      })}
      <style>{`
        @keyframes confetti-fall {
          0%   { transform: translateY(-20px) rotate(0deg); opacity: 0.9; }
          100% { transform: translateY(110vh) rotate(720deg); opacity: 0; }
        }
      `}</style>
    </div>
  );
}

/* ---------- Eligibility info modal ---------- */
function EligibilityModal({ onClose }) {
  const eligible = [
    "McDonald's, KFC, or Popeyes receipt",
    "In-store, drive-thru, takeaway or kiosk order",
    "Uploaded within 3 days of purchase",
    "Total of £9 or more",
    "Photo shows the full receipt clearly (no glare or crops)",
  ];
  const notEligible = [
    "Delivery orders (Uber Eats, Just Eat / JED, Deliveroo)",
    "Any other restaurant or takeaway brand",
    "Screenshots of digital / email receipts",
    "Receipts older than 3 days",
    "Blurry, partial, or edited photos",
    "Handwritten or refunded receipts",
  ];
  return (
    <div
      data-testid="upload-eligibility-modal"
      onClick={onClose}
      style={{
        position: "fixed", inset: 0, zIndex: 220,
        background: "rgba(14, 34, 64, 0.78)",
        display: "flex", alignItems: "center", justifyContent: "center",
        padding: 22, backdropFilter: "blur(6px)", WebkitBackdropFilter: "blur(6px)",
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "100%", maxWidth: 400, maxHeight: "88vh",
          background: "var(--surface)", borderRadius: 26,
          padding: "22px 22px 18px", position: "relative",
          boxShadow: "0 30px 60px -24px rgba(0,0,0,0.4)",
          overflow: "auto",
        }}
      >
        <button
          onClick={onClose}
          data-testid="upload-eligibility-close"
          aria-label="Close"
          style={{
            position: "absolute", top: 12, right: 12,
            width: 34, height: 34, borderRadius: 17,
            background: "var(--surface-soft)", border: "none",
            display: "flex", alignItems: "center", justifyContent: "center",
            cursor: "pointer",
          }}
        >
          <X size={18} color="var(--text-secondary)" />
        </button>

        <div style={{ textAlign: "center", marginBottom: 14 }}>
          <div style={{
            width: 44, height: 44, borderRadius: 14, background: "var(--primary)",
            display: "inline-flex", alignItems: "center", justifyContent: "center",
            marginBottom: 6,
          }}>
            <Info size={22} color="#FFFFFF" />
          </div>
          <h2 style={{
            color: "var(--navy)", fontSize: 18, fontWeight: 800,
            letterSpacing: -0.4, margin: 0,
          }}>
            What counts as a valid receipt?
          </h2>
          <p style={{ color: "var(--text-secondary)", fontSize: 12.5, marginTop: 4 }}>
            Quick guide to what we accept (and what we don't).
          </p>
        </div>

        <div style={{
          background: "rgba(31, 174, 94, 0.08)",
          border: "1px solid rgba(31, 174, 94, 0.30)",
          borderRadius: 14, padding: "12px 14px", marginBottom: 10,
        }}>
          <div style={{
            display: "flex", alignItems: "center", gap: 8, marginBottom: 8,
            color: "#1FAE5E", fontWeight: 800, fontSize: 13, letterSpacing: 0.3,
          }}>
            <CheckCircle2 size={16} /> Eligible
          </div>
          {eligible.map((item, i) => (
            <div key={i} data-testid={`eligibility-yes-${i}`} style={{
              display: "flex", alignItems: "flex-start", gap: 8,
              fontSize: 13, lineHeight: 1.45, color: "var(--text-primary)",
              padding: "4px 0",
            }}>
              <Check size={14} color="#1FAE5E" style={{ flexShrink: 0, marginTop: 3 }} />
              <span>{item}</span>
            </div>
          ))}
        </div>

        <div style={{
          background: "rgba(230, 59, 90, 0.08)",
          border: "1px solid rgba(230, 59, 90, 0.30)",
          borderRadius: 14, padding: "12px 14px",
        }}>
          <div style={{
            display: "flex", alignItems: "center", gap: 8, marginBottom: 8,
            color: "#E63B5A", fontWeight: 800, fontSize: 13, letterSpacing: 0.3,
          }}>
            <XCircle size={16} /> Not eligible
          </div>
          {notEligible.map((item, i) => (
            <div key={i} data-testid={`eligibility-no-${i}`} style={{
              display: "flex", alignItems: "flex-start", gap: 8,
              fontSize: 13, lineHeight: 1.45, color: "var(--text-primary)",
              padding: "4px 0",
            }}>
              <X size={14} color="#E63B5A" style={{ flexShrink: 0, marginTop: 3 }} />
              <span>{item}</span>
            </div>
          ))}
        </div>

        <button
          data-testid="upload-eligibility-got-it"
          onClick={onClose}
          className="btn-primary"
          style={{
            width: "100%", marginTop: 16,
            padding: "12px 16px",
            fontSize: 13, fontWeight: 900, letterSpacing: 0.8, textTransform: "uppercase",
          }}
        >
          Got it
        </button>
      </div>
    </div>
  );
}
